import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ProgressIndicator from './components/ProgressIndicator';
import EventDetailsForm from './components/EventDetailsForm';
import VenueForm from './components/VenueForm';
import TierManagement from './components/TierManagement';
import PublishingOptions from './components/PublishingOptions';
import EventPreview from './components/EventPreview';
import EventService from '../../services/eventService';
import { useAuth } from '../../contexts/AuthContext';

const EventCreation = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishError, setPublishError] = useState(null);
  const [formData, setFormData] = useState({
    // Event Details
    title: '',
    category: '',
    description: '',
    image: null,
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    
    // Venue Information
    venueType: 'physical',
    venueName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'us',
    capacity: '',
    parking: '',
    accessibilityFeatures: [],
    platform: '',
    meetingLink: '',
    virtualCapacity: '',
    
    // Tiers and Pricing
    tiers: [
      {
        id: 1,
        name: 'General Admission',
        price: '',
        capacity: '',
        description: '',
        benefits: [],
        earlyBird: false,
        earlyBirdPrice: '',
        earlyBirdEndDate: ''
      }
    ],
    
    // Publishing Options
    visibility: 'public',
    publishStatus: 'published',
    publishDate: '',
    publishTime: '',
    requireApproval: false,
    allowGroupBookings: false,
    enableWaitlist: false,
    sendConfirmationEmails: true,
    discountCodes: []
  });

  const steps = [
    {
      id: 'details',
      title: 'Event Details',
      description: 'Basic information'
    },
    {
      id: 'venue',
      title: 'Venue & Location',
      description: 'Where it happens'
    },
    {
      id: 'ticketing',
      title: 'Ticketing & Pricing',
      description: 'Tiers and pricing'
    },
    {
      id: 'publishing',
      title: 'Publishing',
      description: 'Final settings'
    }
  ];

  const handleFormChange = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const handleStepClick = (stepIndex) => {
    if (stepIndex <= currentStep) {
      setCurrentStep(stepIndex);
    }
  };

  const handleNext = () => {
    if (currentStep < steps?.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Enhanced handlePublish method with Supabase integration
  const handlePublish = async () => {
    try {
      setIsPublishing(true);
      setPublishError(null);

      if (!userProfile?.id) {
        setPublishError('You must be logged in to publish an event');
        return;
      }

      // Prepare event data for Supabase
      const eventData = {
        title: formData?.title,
        description: formData?.description,
        date: formData?.startDate ? new Date(`${formData?.startDate}T${formData?.startTime || '09:00'}`)?.toISOString() : null,
        endDate: formData?.endDate ? new Date(`${formData?.endDate}T${formData?.endTime || '17:00'}`)?.toISOString() : null,
        location: formData?.venueType === 'physical' ? 
          `${formData?.address ? formData?.address + ', ' : ''}${formData?.city || ''}, ${formData?.state || ''}`?.trim()?.replace(/^,\s*|,\s*$/, '') :
          formData?.platform || 'Virtual Event',
        venueName: formData?.venueType === 'physical' ? formData?.venueName : formData?.platform,
        category: formData?.category,
        maxCapacity: formData?.venueType === 'physical' ? 
          parseInt(formData?.capacity) || null : 
          parseInt(formData?.virtualCapacity) || null,
        imageUrl: formData?.image,
        status: formData?.publishStatus === 'published' ? 'published' : 'draft',
        isFeatured: false // Can be set based on admin privileges later
      };

      // Create event via EventService
      const { data: createdEvent, error: eventError } = await EventService?.createEvent(eventData, userProfile);

      if (eventError) {
        throw new Error(eventError);
      }

      if (!createdEvent?.id) {
        throw new Error('Failed to create event - no ID returned');
      }

      // Create event tickets/tiers if event was created successfully
      if (formData?.tiers && formData?.tiers?.length > 0) {
        const ticketPromises = formData?.tiers?.map(async (tier) => {
          const ticketData = {
            event_id: createdEvent?.id,
            ticket_name: tier?.name || 'General Admission',
            ticket_type: tier?.price && parseFloat(tier?.price) > 0 ? 'paid' : 'free',
            price: tier?.price ? parseFloat(tier?.price) : 0,
            quantity_available: tier?.capacity ? parseInt(tier?.capacity) : eventData?.maxCapacity || 100,
            description: tier?.description || '',
            sale_start_date: tier?.earlyBird && tier?.earlyBirdEndDate ? 
              new Date()?.toISOString() : 
              eventData?.date,
            sale_end_date: tier?.earlyBird && tier?.earlyBirdEndDate ? 
              new Date(tier?.earlyBirdEndDate)?.toISOString() : 
              eventData?.date
          };

          return EventService?.createEventTicket(ticketData);
        });

        const ticketResults = await Promise.allSettled(ticketPromises);
        
        // Log any ticket creation errors but don't fail the entire process
        ticketResults?.forEach((result, index) => {
          if (result?.status === 'rejected') {
            console.error(`Failed to create ticket ${index + 1}:`, result?.reason);
          }
        });
      }

      // Show success message and redirect
      navigate('/event-management', { 
        state: { 
          message: `Event ${formData?.publishStatus === 'published' ? 'published' : 'saved'} successfully!`,
          eventId: createdEvent?.id,
          eventData: { ...formData, id: createdEvent?.id }
        }
      });

    } catch (error) {
      console.error('Error publishing event:', error);
      setPublishError(error?.message || 'Failed to publish event. Please try again.');
    } finally {
      setIsPublishing(false);
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <EventDetailsForm
            formData={formData}
            onFormChange={handleFormChange}
            onNext={handleNext}
          />
        );
      case 1:
        return (
          <VenueForm
            formData={formData}
            onFormChange={handleFormChange}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 2:
        return (
          <TierManagement
            formData={formData}
            onFormChange={handleFormChange}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 3:
        return (
          <PublishingOptions
            formData={formData}
            onFormChange={handleFormChange}
            onPrevious={handlePrevious}
            onPublish={handlePublish}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="max-w-6xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">Create New Event</h1>
                <p className="text-muted-foreground">
                  Set up your event with our guided workflow
                </p>
              </div>
              
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowPreview(true)}
                  disabled={isPublishing}
                >
                  <Icon name="Eye" size={16} className="mr-2" />
                  Preview
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/event-management')}
                  disabled={isPublishing}
                >
                  <Icon name="X" size={16} />
                  Cancel
                </Button>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {publishError && (
            <div className="mb-6 bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <Icon name="AlertCircle" size={20} className="text-destructive" />
                <div>
                  <h4 className="font-medium text-destructive">Publishing Failed</h4>
                  <p className="text-sm text-destructive/80 mt-1">{publishError}</p>
                </div>
              </div>
            </div>
          )}

          {/* Progress Indicator */}
          <ProgressIndicator
            currentStep={currentStep}
            steps={steps}
            onStepClick={handleStepClick}
          />

          {/* Current Step Content */}
          <div className="mb-8">
            {renderCurrentStep()}
          </div>

          {/* Publishing Status */}
          {isPublishing && (
            <div className="mb-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent"></div>
                <div>
                  <h4 className="font-medium text-blue-900">Publishing Event...</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Creating your event and setting up tickets. This may take a moment.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="bg-white border border-border rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Save" size={16} />
                  <span>Auto-saved {new Date()?.toLocaleTimeString()}</span>
                </div>
                
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Users" size={16} />
                  <span>
                    {formData?.tiers ? 
                      formData?.tiers?.reduce((total, tier) => total + (parseInt(tier?.capacity) || 0), 0) 
                      : 0
                    } total capacity
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleFormChange({ publishStatus: 'draft' })}
                  disabled={isPublishing}
                >
                  <Icon name="FileText" size={16} className="mr-2" />
                  Save as Draft
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPreview(true)}
                  disabled={isPublishing}
                >
                  <Icon name="Eye" size={16} className="mr-2" />
                  Preview Event
                </Button>
              </div>
            </div>
          </div>

          {/* Help Section */}
          <div className="mt-8 bg-accent p-6 rounded-lg">
            <div className="flex items-start gap-4">
              <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-lg">
                <Icon name="HelpCircle" size={20} color="white" />
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-2">Need Help?</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Our event creation guide covers best practices for successful events.
                </p>
                <div className="flex gap-3">
                  <Button variant="outline" size="sm" disabled={isPublishing}>
                    <Icon name="Book" size={14} className="mr-2" />
                    View Guide
                  </Button>
                  <Button variant="outline" size="sm" disabled={isPublishing}>
                    <Icon name="MessageCircle" size={14} className="mr-2" />
                    Get Support
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Event Preview Modal */}
      {showPreview && (
        <EventPreview
          formData={formData}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
};

export default EventCreation;